import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetMainMenuItemsParams,
  GridApi,
  GridOptions,
  MenuItemDef,
} from 'ag-grid-community';
import { v4 as uuid } from 'uuid';
import { useMixpanel } from '../../../contexts';
import { MixpanelEvent } from '../../../tokens';
import { logger } from '../../../utils';
import { createExportFileName } from '../../../utils/fileExport';
import { IconName } from '../../Icons/IconNames';
import { agGridMenuSeparator, type ContextMenuOptions, type ExportOptions } from '../types';

import { cloneDeep, isFunction } from 'lodash-es';
import { useCallbackRef } from '../../../hooks';
import { getCustomColDefProperties } from '../columns/baseColumn';
import type { ColumnDynamics } from '../columns/types';
import { assembleCustomAndDefaultContextMenuItems, useGetDefaultContextMenuItems } from '../contextMenu';
import { buildBlotterMainMenuItems } from '../mainMenuItems';
import { addColumn, getSaveColumnSettingsFn, removeColumn as removeColumnFromGrid } from '../useColumnDynamics/actions';
import type { ColumnDynamicsSpec } from '../useColumnDynamics/ColumnDynamics';
import { getColumnDynamicsState } from '../useColumnDynamics/ColumnDynamics';
import { getAllNodesInGroup, removeUnnecessarySeparators, resolveSyncMenuItems, safeGridApi } from '../utils';
import type { UseBlotterTable, UseBlotterTableProps } from './types';

export interface UseBlotterTableMenusParams<TRow extends object> {
  gridApi: GridApi<TRow> | undefined;
  getExtraMainMenuItems?: GridOptions['getMainMenuItems'];

  columnDynamicsParams?: UseBlotterTableProps<TRow>['columnDynamicsParams'];

  contextMenuOptions?: ContextMenuOptions<TRow>;
  contextMenuHandleClickJson: (item?: unknown) => void;

  exportOptions?: {
    csvOptions: {
      exportFunc: UseBlotterTable<unknown>['exportDataAsCSV'];
      params?: ExportOptions['csvParams'];
    };
    excelOptions: {
      exportFunc: UseBlotterTable<unknown>['exportDataAsExcel'];
      params?: ExportOptions['excelParams'];
    };
    baseParams?: ExportOptions['baseParams'];
    name: string;
    tabLabel?: string;
  };
}

export const useBlotterTableMenus = <TRow extends object>({
  gridApi,
  getExtraMainMenuItems,
  exportOptions,
  columnDynamicsParams,
  contextMenuOptions,
  contextMenuHandleClickJson,
}: UseBlotterTableMenusParams<TRow>) => {
  // Default context menu items
  const mixpanel = useMixpanel();

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems(
    contextMenuHandleClickJson,
    contextMenuOptions?.dataDisplayCallback,
    contextMenuOptions?.relatedEntities
  );

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<TRow>) =>
    assembleCustomAndDefaultContextMenuItems(
      params,
      getDefaultContextMenuItems,
      contextMenuOptions?.getCustomContextMenuItems
    )
  );

  // This function intercepts the getting of context menu items (called on context menu open) and performs some uniform logic
  // Keeping it stable to lessen the amount of possible reasons for gridOptions to update. This is just an event handler, too, so it can be stateless.
  const getContextMenuItemsResolved: NonNullable<GridOptions<TRow>['getContextMenuItems']> = useCallbackRef(
    (params: GetContextMenuItemsParams<TRow>) => {
      const rightClickedNode = params.node;
      let isClickSelectionEnabled = true;
      const rowSelectionOptions = params.api.getGridOption('rowSelection');
      if (typeof rowSelectionOptions !== 'string' && typeof rowSelectionOptions?.enableClickSelection === 'boolean') {
        // [DEAL-5219] If click selection is disabled, we do not want to trigger the internal selection logic below.
        isClickSelectionEnabled = rowSelectionOptions.enableClickSelection;
      }

      if (rightClickedNode == null) {
        // Shouldn't happen
        logger.warn('No right clicked node found when attempting to build the BlotterTable AgGrid Context Menu');
        return [];
      }
      const selectedNodes = params.api.getSelectedNodes().filter(node => node.displayed);
      const isRightClickedNodeInSelection = selectedNodes.some(node => node.id === rightClickedNode.id);

      if (!isRightClickedNodeInSelection) {
        params.api.deselectAll();
      }

      if (rightClickedNode.group && isClickSelectionEnabled) {
        // If we have right clicked on a group row, we make sure to select the group row itself as well as all of its children before proceeding
        const nodes = [rightClickedNode, ...getAllNodesInGroup(rightClickedNode)];
        params.api.setNodesSelected({ nodes, newValue: true, source: 'rowClicked' });
      } else if (!rightClickedNode.rowPinned && isClickSelectionEnabled) {
        // Case of right-clicking on a normal row (also making sure its not pinned, aggrid doesn't like that), we select it
        rightClickedNode.setSelected(true, false);
      }

      const resolvedContextMenuItems = resolveSyncMenuItems(getContextMenuItems(params), 'BlotterTable');

      const items: (DefaultMenuItem | MenuItemDef<TRow>)[] = [...resolvedContextMenuItems];

      if (exportOptions) {
        const { csvOptions, excelOptions, baseParams, tabLabel, name } = exportOptions;
        items.push(agGridMenuSeparator);
        const exportSubMenu: MenuItemDef = {
          name: 'Export',
          icon: `<i class="ag-icon ${IconName.DocumentDownload}"/>`,
          subMenu: [
            {
              name: `Export CSV`,
              icon: `<i class="ag-icon ${IconName.DocumentDownload}"/>`,
              action: () => {
                mixpanel.track(MixpanelEvent.ExportRows);
                csvOptions!.exportFunc({
                  fileName: createExportFileName({ name: name || 'Export', tabLabel, mode: 'CSV' }),
                  ...(isFunction(baseParams) ? baseParams(safeGridApi(gridApi)) : baseParams),
                  ...csvOptions!.params,
                });
              },
            },
            {
              name: `Export Excel`,
              icon: `<i class="ag-icon ${IconName.DocumentDownload}"/>`,
              action: () => {
                mixpanel.track(MixpanelEvent.ExportRows);
                excelOptions!.exportFunc({
                  fileName: createExportFileName({ name: name || 'Export', tabLabel, mode: 'Excel' }),
                  ...(isFunction(baseParams) ? baseParams(safeGridApi(gridApi)) : baseParams),
                  ...excelOptions!.params,
                });
              },
            },
          ],
        };
        items.push(exportSubMenu);
      }

      return removeUnnecessarySeparators(items);
    }
  );

  // Main menu
  // Made stable to avoid another reason for gridOptions to update. It is also just an event handler, so having it be stateless
  // and read its dependencies only when it is called is okay.
  const getMainMenuItems: NonNullable<GridOptions<TRow>['getMainMenuItems']> = useCallbackRef(
    (params: GetMainMenuItemsParams<TRow>) => {
      return buildBlotterMainMenuItems({
        params,
        getExtraMainMenuItems,
        columnDynamicsMenuItems: columnDynamicsParams
          ? getColumnDynamicsMainMenuItems(columnDynamicsParams.columnDynamicsSpecs, params)
          : undefined,
        onExpandAllRows: () => mixpanel.track(MixpanelEvent.ExpandAllRows),
        onCollapseAllRows: () => mixpanel.track(MixpanelEvent.CollapseAllRows),
      });
    }
  );

  return { getMainMenuItems, getContextMenuItems: getContextMenuItemsResolved };
};

/** When cloning a column with dynamics, use this function to correctly get the new cloned dynamic descriptor */
export function cloneColumnDynamics<StateType>(
  sourceDynamics: ColumnDynamics<StateType>,
  colID: string
): ColumnDynamics<StateType> {
  const sourceSettings = getColumnDynamicsState(sourceDynamics);
  return {
    colID,
    spec: sourceDynamics.spec,
    removable: true,
    initialState: cloneDeep(sourceSettings),
    state: cloneDeep(sourceSettings),
  };
}

/** For columns that support dynamics, this function collects the MenuItemDefs necessary for the user to be able to interact with the mechanism */
function getColumnDynamicsMainMenuItems(specs: ColumnDynamicsSpec[], params: GetMainMenuItemsParams): MenuItemDef[] {
  const clickedColumn = params.column;
  if (!clickedColumn) {
    return [];
  }

  const addItem: MenuItemDef = {
    name: 'Add Column',
    subMenu: specs.map(spec => ({
      name: spec.label,
      action: () => {
        const initialSettings = spec.getInitialSettings();
        const dynamics: ColumnDynamics = {
          colID: uuid(),
          spec: spec.type,
          removable: true,
          initialState: initialSettings,
          state: initialSettings,
        };
        addColumn(params.api, spec.getColumn(dynamics));
        if (spec.openSettingsOnCreation) {
          spec.openSettings?.(getColumnDynamicsState(dynamics), getSaveColumnSettingsFn(params.api, dynamics, spec));
        }
      },
    })),
  };

  const items = [addItem];

  const clickedColumnCustomProperties = getCustomColDefProperties(clickedColumn.getColDef());
  const dynamics = clickedColumnCustomProperties?.dynamics;
  const spec = specs.find(spec => spec.type === dynamics?.spec);
  if (spec && dynamics) {
    items.push({
      name: 'Clone Column',
      action: () => {
        const clonedDynamics = cloneColumnDynamics(dynamics, uuid());
        addColumn(params.api, spec.getColumn(clonedDynamics));
      },
    });
  }

  if (spec?.openSettings && dynamics) {
    // we are a column with dynamics, so we can edit and remove ourselves (if removable is true)
    items.push({
      name: 'Edit Column',
      action: () =>
        spec.openSettings?.(getColumnDynamicsState(dynamics), getSaveColumnSettingsFn(params.api, dynamics, spec)),
    });
  }

  if (dynamics?.removable) {
    const removeColId = dynamics.colID !== clickedColumn.getColId() ? dynamics.colID : clickedColumn.getColId();
    items.push({
      name: 'Remove Column',
      action: () => removeColumnFromGrid(params.api, removeColId),
    });
  }

  return items;
}

/** Removes a column from the grid's  columnDefs */
export function removeColumn(unsafeApi: GridApi, colIDToRemove: string) {
  removeColumnFromGrid(unsafeApi, colIDToRemove);
}
